/**
 * Component for displaying indicator data from Regionalstatistik and INKAR
 */

import { ChevronDown, ChevronUp, BarChart3, Loader2, ExternalLink } from 'lucide-react'
import { useState, useMemo } from 'react'
import { useEntityIndicators } from '../hooks/queries'
import type { IndicatorGroup, IndicatorValue } from '../services/api'
import { formatIndicatorValue } from '../lib/utils'

// All supported entity types including BBSR regions
type EntityType =
  | 'municipality'
  | 'county'
  | 'state'
  | 'government_district'
  | 'federal'
  | 'metropolitan_region'
  | 'major_city_region'
  | 'ihk_district'
  | 'bbsr_middle_area'
  | 'county_region'
  | 'spatial_planning_region'
  | 'labor_market_region'

interface IndicatorSectionProps {
  entityType: EntityType
  ags: string
  title?: string
  /** Maximum reference year for filtering (typically edition - 1) */
  maxYear?: number
}

// Provider configuration with display names and sources
const providerConfig: Record<string, {
  title: string
  source: string
  sourceUrl: string
  headerColor: string
}> = {
  regionalstatistik: {
    title: 'Regionalstatistik',
    source: 'Statistische Ämter des Bundes und der Länder',
    sourceUrl: 'https://www.regionalstatistik.de',
    headerColor: 'text-blue-600',
  },
  inkar: {
    title: 'INKAR (Raumbeobachtung)',
    source: 'Bundesamt für Bauwesen und Raumordnung (BBR)',
    sourceUrl: 'https://www.inkar.de',
    headerColor: 'text-orange-600',
  },
  prognos: {
    title: 'Prognos',
    source: 'Prognos AG',
    sourceUrl: 'https://www.prognos.com/zukunftsatlas',
    headerColor: 'text-emerald-600',
  },
  mb_research: {
    title: 'MB-Research',
    source: 'MB-Research GmbH',
    sourceUrl: 'https://www.mb-research.de',
    headerColor: 'text-amber-600',
  },
  csv_import: {
    title: 'CSV Import',
    source: 'Externer Datenanbieter',
    sourceUrl: '',
    headerColor: 'text-purple-600',
  },
}

// Component for displaying a single indicator row with year column
function IndicatorRow({ indicator, showYear }: { indicator: IndicatorValue; showYear: boolean }) {
  return (
    <div className="flex items-center gap-4 px-4 py-2.5 hover:bg-gray-50">
      <span className="flex-1 text-sm text-gut8-anthracite">
        {indicator.indicator_name}
      </span>
      {showYear && (
        <span className="text-sm text-text-muted tabular-nums w-12 text-center">
          {indicator.year || indicator.reference_year}
        </span>
      )}
      <span className="text-sm font-medium text-gut8-anthracite tabular-nums text-right min-w-[80px]">
        {indicator.value_text || formatIndicatorValue(indicator.value, indicator.unit)}
      </span>
    </div>
  )
}

// Component for subcategory group (expandable)
function SubcategoryGroup({
  subcategory,
  indicators,
  showYear
}: {
  subcategory: string
  indicators: IndicatorValue[]
  showYear: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div className="ml-4 border-l-2 border-orange-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-orange-50 transition-colors text-left"
      >
        <span className="flex-1 text-sm font-medium text-orange-700">
          {subcategory}
        </span>
        <span className="text-xs text-text-muted">
          {indicators.length}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        )}
      </button>
      {isExpanded && (
        <div className="divide-y divide-border-gut8-light bg-white">
          {indicators.map((indicator) => (
            <IndicatorRow
              key={`${indicator.indicator_id}-${indicator.year || indicator.reference_year}`}
              indicator={indicator}
              showYear={showYear}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Component for category group with optional subcategories
function CategoryGroup({
  category,
  indicators,
  showYear
}: {
  category: string
  indicators: IndicatorValue[]
  showYear: boolean
}) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Group indicators by subcategory
  const grouped = useMemo(() => {
    const bySubcategory: Record<string, IndicatorValue[]> = {}
    const noSubcategory: IndicatorValue[] = []

    for (const ind of indicators) {
      if (ind.subcategory) {
        if (!bySubcategory[ind.subcategory]) {
          bySubcategory[ind.subcategory] = []
        }
        bySubcategory[ind.subcategory].push(ind)
      } else {
        noSubcategory.push(ind)
      }
    }

    // Sort indicators within each group by name
    for (const key of Object.keys(bySubcategory)) {
      bySubcategory[key].sort((a, b) => a.indicator_name.localeCompare(b.indicator_name))
    }
    noSubcategory.sort((a, b) => a.indicator_name.localeCompare(b.indicator_name))

    return { bySubcategory, noSubcategory }
  }, [indicators])

  const subcategoryNames = Object.keys(grouped.bySubcategory).sort()

  return (
    <div className="border border-border-gut8-light rounded-lg overflow-hidden">
      {/* Category Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-orange-50 hover:bg-orange-100 transition-colors text-left"
      >
        <BarChart3 className="w-5 h-5 flex-shrink-0 text-orange-500" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gut8-anthracite truncate">
            {category}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            {indicators.length} Indikator{indicators.length !== 1 ? 'en' : ''}
            {subcategoryNames.length > 0 && ` · ${subcategoryNames.length} Unterkategorie${subcategoryNames.length !== 1 ? 'n' : ''}`}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-text-muted flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="divide-y divide-border-gut8-light">
          {/* Table header for year column */}
          {showYear && (
            <div className="flex items-center gap-4 px-4 py-2 bg-gut8-bg-muted text-xs text-text-muted font-medium">
              <span className="flex-1">Indikator</span>
              <span className="w-12 text-center">Jahr</span>
              <span className="min-w-[80px] text-right">Wert</span>
            </div>
          )}

          {/* Indicators without subcategory */}
          {grouped.noSubcategory.length > 0 && (
            <div className="divide-y divide-border-gut8-light">
              {grouped.noSubcategory.map((indicator) => (
                <IndicatorRow
                  key={`${indicator.indicator_id}-${indicator.year || indicator.reference_year}`}
                  indicator={indicator}
                  showYear={showYear}
                />
              ))}
            </div>
          )}

          {/* Subcategory groups */}
          {subcategoryNames.map((subcategory) => (
            <SubcategoryGroup
              key={subcategory}
              subcategory={subcategory}
              indicators={grouped.bySubcategory[subcategory]}
              showYear={showYear}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Original card for Regionalstatistik (grouped by data source)
function IndicatorGroupCard({ group }: { group: IndicatorGroup }) {
  const [isExpanded, setIsExpanded] = useState(true)
  const { data_source, indicators } = group

  // Format time reference
  const timeRef = data_source.time_label && data_source.time_value
    ? `${data_source.time_label}: ${data_source.time_value}`
    : `Jahr ${data_source.reference_year}`

  // Determine display name: dataset_name + edition for CSV imports, statistics_label for GENESIS
  const displayName = useMemo(() => {
    if (data_source.provider === 'inkar') {
      return `INKAR ${data_source.reference_year}`
    }
    if (data_source.statistics_label) {
      return data_source.statistics_label
    }
    // CSV imports: use dataset_name + edition from DataSource
    if (data_source.dataset_name) {
      return data_source.edition
        ? `${data_source.dataset_name} ${data_source.edition}`
        : data_source.dataset_name
    }
    if (data_source.table_code) {
      return `Tabelle ${data_source.table_code}`
    }
    return `Datenquelle ${data_source.id}`
  }, [data_source])

  return (
    <div className="border border-border-gut8-light rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gut8-bg-muted hover:bg-gray-100 transition-colors text-left"
      >
        <BarChart3 className={`w-5 h-5 flex-shrink-0 ${data_source.provider === 'inkar' ? 'text-orange-500' : 'text-primary'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gut8-anthracite truncate">
            {displayName}
          </div>
          <div className="text-xs text-text-muted mt-0.5">
            {timeRef} · {indicators.length} Indikator{indicators.length !== 1 ? 'en' : ''}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-text-muted flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
        )}
      </button>

      {/* Indicators */}
      {isExpanded && (
        <div className="divide-y divide-border-gut8-light">
          {indicators.map((indicator: IndicatorValue) => (
            <div
              key={indicator.indicator_id}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50"
            >
              <span className="text-sm text-gut8-anthracite">
                {indicator.indicator_name}
              </span>
              <span className="text-sm font-medium text-gut8-anthracite tabular-nums">
                {indicator.value_text || formatIndicatorValue(indicator.value, indicator.unit)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// INKAR Section - groups by category/subcategory
function InkarSection({ groups, customTitle }: { groups: IndicatorGroup[]; customTitle?: string }) {
  const config = providerConfig.inkar

  // Collect all indicators from all INKAR groups
  const allIndicators = useMemo(() => {
    const indicators: IndicatorValue[] = []
    for (const group of groups) {
      indicators.push(...group.indicators)
    }
    return indicators
  }, [groups])

  // Check if we have multiple years
  const hasMultipleYears = useMemo(() => {
    const years = new Set(allIndicators.map(i => i.year || i.reference_year))
    return years.size > 1
  }, [allIndicators])

  // Group by category
  const groupedByCategory = useMemo(() => {
    const byCategory: Record<string, IndicatorValue[]> = {}
    const noCategory: IndicatorValue[] = []

    for (const ind of allIndicators) {
      if (ind.category) {
        if (!byCategory[ind.category]) {
          byCategory[ind.category] = []
        }
        byCategory[ind.category].push(ind)
      } else {
        noCategory.push(ind)
      }
    }

    return { byCategory, noCategory }
  }, [allIndicators])

  const categoryNames = Object.keys(groupedByCategory.byCategory).sort()
  const totalIndicators = allIndicators.length

  return (
    <div className="card-gut8">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 -mt-6 mb-4">
        <BarChart3 className={`w-6 h-6 ${config.headerColor}`} />
        <div className="flex-1">
          <span className="font-semibold text-gut8-anthracite">
            {customTitle || config.title}
          </span>
          <span className="text-sm text-text-muted ml-3">
            {totalIndicators} Indikator{totalIndicators !== 1 ? 'en' : ''}
            {categoryNames.length > 0 && ` · ${categoryNames.length} Kategorie${categoryNames.length !== 1 ? 'n' : ''}`}
          </span>
        </div>
      </div>

      {/* Category Groups */}
      <div className="space-y-4">
        {/* Indicators without category (fallback) */}
        {groupedByCategory.noCategory.length > 0 && (
          <CategoryGroup
            category="Sonstige"
            indicators={groupedByCategory.noCategory}
            showYear={hasMultipleYears}
          />
        )}

        {/* Category groups */}
        {categoryNames.map((category) => (
          <CategoryGroup
            key={category}
            category={category}
            indicators={groupedByCategory.byCategory[category]}
            showYear={hasMultipleYears}
          />
        ))}
      </div>

      {/* Footer - Data Source Info */}
      <div className="mt-4 pt-4 border-t border-border-gut8-light">
        <p className="text-xs text-text-muted">
          Quelle: {config.source}
          {config.sourceUrl && (
            <a
              href={config.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-primary hover:underline inline-flex items-center gap-0.5"
            >
              {config.sourceUrl.replace('https://', '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </p>
      </div>
    </div>
  )
}

// Provider section component - shows all groups from one provider (for Regionalstatistik)
function ProviderSection({
  provider,
  groups,
}: {
  provider: string
  groups: IndicatorGroup[]
}) {
  const config = providerConfig[provider] || {
    title: provider,
    source: provider,
    sourceUrl: '',
    headerColor: 'text-gray-600',
  }

  const totalIndicators = groups.reduce((sum, g) => sum + g.indicators.length, 0)

  return (
    <div className="card-gut8">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 -mt-6 mb-4">
        <BarChart3 className={`w-6 h-6 ${config.headerColor}`} />
        <div className="flex-1">
          <span className="font-semibold text-gut8-anthracite">
            {config.title}
          </span>
          <span className="text-sm text-text-muted ml-3">
            {totalIndicators} Indikator{totalIndicators !== 1 ? 'en' : ''}
          </span>
        </div>
      </div>

      {/* Indicator Groups */}
      <div className="space-y-4">
        {groups.map((group) => (
          <IndicatorGroupCard key={group.data_source.id} group={group} />
        ))}
      </div>

      {/* Footer - Data Source Info */}
      <div className="mt-4 pt-4 border-t border-border-gut8-light">
        <p className="text-xs text-text-muted">
          Quelle: {config.source}
          {config.sourceUrl && (
            <a
              href={config.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-primary hover:underline inline-flex items-center gap-0.5"
            >
              {config.sourceUrl.replace('https://', '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </p>
      </div>
    </div>
  )
}

export default function IndicatorSection({ entityType, ags, title, maxYear }: IndicatorSectionProps) {
  const { data, isLoading, error } = useEntityIndicators(entityType, ags, undefined, maxYear)

  // Group by provider
  const groupsByProvider = useMemo(() => {
    if (!data || data.groups.length === 0) return {}

    const result: Record<string, IndicatorGroup[]> = {}
    for (const group of data.groups) {
      const provider = group.data_source.provider || 'unknown'
      if (!result[provider]) {
        result[provider] = []
      }
      result[provider].push(group)
    }
    return result
  }, [data])

  // Order providers: regionalstatistik first, then inkar, then others
  const providerOrder = ['regionalstatistik', 'inkar', 'csv_import']
  const sortedProviders = useMemo(() => {
    const providers = Object.keys(groupsByProvider)
    return providers.sort((a, b) => {
      const aIndex = providerOrder.indexOf(a)
      const bIndex = providerOrder.indexOf(b)
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
  }, [groupsByProvider])

  if (isLoading) {
    return (
      <div className="card-gut8">
        <div className="flex items-center gap-3 px-6 py-4 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 -mt-6 mb-4">
          <BarChart3 className="w-6 h-6 text-primary" />
          <span className="font-semibold text-gut8-anthracite">
            {title || 'Indikatoren'}
          </span>
        </div>
        <div className="flex items-center justify-center py-8 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Lade Indikatoren...
        </div>
      </div>
    )
  }

  if (error) {
    return null // Silently fail if no indicators available
  }

  if (!data || data.groups.length === 0) {
    return null // Don't show section if no data
  }

  // If there's only one provider and a custom title is given, use the old single-section layout
  if (sortedProviders.length === 1 && title) {
    const provider = sortedProviders[0]

    // Use INKAR-specific layout if provider is INKAR
    if (provider === 'inkar') {
      return <InkarSection groups={groupsByProvider[provider]} customTitle={title} />
    }

    const config = providerConfig[provider] || {
      title: provider,
      source: provider,
      sourceUrl: '',
      headerColor: 'text-gray-600',
    }

    return (
      <div className="card-gut8">
        <div className="flex items-center gap-3 px-6 py-4 bg-gut8-bg-muted border-b border-border-gut8-light -mx-6 -mt-6 mb-4">
          <BarChart3 className={`w-6 h-6 ${config.headerColor}`} />
          <div className="flex-1">
            <span className="font-semibold text-gut8-anthracite">
              {title}
            </span>
            <span className="text-sm text-text-muted ml-3">
              {data.total_indicators} Indikator{data.total_indicators !== 1 ? 'en' : ''}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {data.groups.map((group) => (
            <IndicatorGroupCard key={group.data_source.id} group={group} />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border-gut8-light">
          <p className="text-xs text-text-muted">
            Quelle: {config.source}
            {config.sourceUrl && (
              <a
                href={config.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 text-primary hover:underline inline-flex items-center gap-0.5"
              >
                {config.sourceUrl.replace('https://', '')}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </p>
        </div>
      </div>
    )
  }

  // Multiple providers: show separate sections with specialized INKAR handling
  return (
    <div className="space-y-6">
      {sortedProviders.map((provider) => {
        if (provider === 'inkar') {
          return <InkarSection key={provider} groups={groupsByProvider[provider]} customTitle={title} />
        }
        return (
          <ProviderSection
            key={provider}
            provider={provider}
            groups={groupsByProvider[provider]}
          />
        )
      })}
    </div>
  )
}
